import { LitElement, css, html } from 'lit'
import '../term-card/ct-term-card.js'

import {
  getProgramTerms,
} from '../../services/program-terms.js'

import {
  getAvailability,
} from '../../utils/get-availability.js'

import {
  getPilotById,
} from '../../data/pilots.js'

import {
  formatCurrentPrice,
  formatDepositNote,
} from '../../utils/format-price.js'

export class CtOfferTerms extends LitElement {
  static properties = {
    programKey: {
      type: String,
      attribute: 'program-key',
    },

    terms: {
      state: true,
    },

    loading: {
      state: true,
    },

    errorMessage: {
      state: true,
    },
  }

  constructor() {
    super()

    this.programKey = ''
    this.terms = []
    this.loading = false
    this.errorMessage = ''
    this.detailsOpenSequence = 0
  }

  static styles = css`
    :host {
      display: block;
      width: 100%;
    }

    .terms {
      display: flex;
      width: 100%;
      flex-wrap: wrap;
      align-items: flex-start;
      justify-content: center;
      gap: 20px;
      padding: 10px 3%;
      overflow: visible;
      box-sizing: border-box;
    }

    /* Telefon pionowo i poziomo: jedna karta wykorzystuje cały dostępny rząd. */
    ct-term-card {
      display: block;
      flex: 0 0 100%;
      width: 100%;
      min-width: 0;
      max-width: none;
      box-sizing: border-box;
    }

    .message {
      margin: 0;
      padding: 20px;
      border-radius: 8px;
      background: #f5f5f5;
      color: #666666;
      font-family: Poppins, Arial, sans-serif;
      text-align: center;
    }

    .message--error {
      color: #a12a2a;
      background: #fff1f1;
    }

    /* Telefon poziomo: po osiągnięciu 470px karta przestaje się rozszerzać. */
    @media (min-width: 481px) and (max-width: 768px) {
      ct-term-card {
        flex-basis: 470px;
        width: 470px;
        max-width: 100%;
      }
    }

    /* Tablet: dwa elastyczne, równe kafle w rzędzie. */
    @media (min-width: 769px) and (max-width: 1199px) {
      ct-term-card {
        flex-basis: calc((100% - 20px) / 2);
        width: calc((100% - 20px) / 2);
      }
    }

    /* PC: trzy stałe karty 380px mieszczą się w bloku 1200px. */
    @media (min-width: 1200px) {
      .terms {
        padding-right: 10px;
        padding-left: 10px;
      }

      ct-term-card {
        flex: 0 0 380px;
        width: 380px;
      }

    }
  `

  updated(changedProperties) {
    if (changedProperties.has('programKey')) {
      this.termsReady = this.loadTerms()
    }

  }

  getStickyEndElement() {
    const firstCard = this.renderRoot?.querySelector('ct-term-card')
    return firstCard?.renderRoot?.querySelector('.booking') ?? null
  }

  async whenTermsReady() {
    await this.updateComplete
    await this.termsReady
    await this.updateComplete
    await Promise.all(
      [...this.renderRoot.querySelectorAll('ct-term-card')]
        .map(card => card.updateComplete)
    )
  }

  /**
   * Pozostawia otwarte szczegóły tylko w karcie klikniętej przez użytkownika.
   */
  async handleDetailsOpen(event) {
    const openedCard = event.detail?.card
    const shouldPreservePosition =
      window.matchMedia?.('(max-width: 1199px)')
        .matches ?? false
    const positionBeforeClosing =
      openedCard?.getBoundingClientRect().top
    const currentSequence =
      ++this.detailsOpenSequence

    const closingCards = [
      ...this.renderRoot.querySelectorAll(
        'ct-term-card'
      ),
    ]
      .filter(card => card !== openedCard)
      .map(card => card.closeDetails?.())

    await Promise.all(closingCards)

    if (
      !shouldPreservePosition ||
      !openedCard?.isConnected ||
      currentSequence !== this.detailsOpenSequence
    ) {
      return
    }

    // Dwie klatki dają przeglądarce i WebWave czas na przeliczenie układu.
    await new Promise(resolve => {
      requestAnimationFrame(() => {
        requestAnimationFrame(resolve)
      })
    })

    const positionAfterClosing =
      openedCard.getBoundingClientRect().top
    const scrollCorrection =
      positionAfterClosing - positionBeforeClosing

    if (Math.abs(scrollCorrection) > 1) {
      // Natychmiastowa korekta utrzymuje klikniętą kartę w tym samym miejscu.
      window.scrollBy({
        top: scrollCorrection,
        left: 0,
        behavior: 'instant',
      })
    }
  }

  async loadTerms() {
    this.loading = true
    this.errorMessage = ''

    try {
      const result = await getProgramTerms(
        this.programKey
      )

      if (!result.program) {
        this.terms = []
        this.errorMessage =
          'Nie znaleziono terminów dla tego programu.'
        return
      }

      if (result.omittedTripIds.length > 0) {
        console.info(
          'Pominięto niewidoczne terminy:',
          result.omittedTripIds
        )
      }

      if (result.errors.length > 0) {
        console.warn(
          'Nie udało się pobrać części terminów:',
          result.errors
        )
      }

      this.terms = result.terms

      if (
        this.terms.length === 0 &&
        result.errors.length > 0
      ) {
        this.errorMessage =
          'Nie udało się pobrać terminów. Odśwież stronę i spróbuj ponownie.'
      }
    } catch (error) {
      console.error(
        'Błąd pobierania terminów:',
        error
      )

      this.terms = []
      this.errorMessage =
        'Nie udało się pobrać dostępnych terminów.'
    } finally {
      this.loading = false
    }
  }

  render() {
    if (this.loading) {
      return html`
        <p class="message">
          Pobieranie terminów...
        </p>
      `
    }

    if (this.errorMessage) {
      return html`
        <p class="message message--error">
          ${this.errorMessage}
        </p>
      `
    }

    if (this.terms.length === 0) {
      return html`
        <p class="message">
          Obecnie nie ma dostępnych terminów.
        </p>
      `
    }

    return html`
      <section
        class="terms"
        aria-label="Dostępne terminy wyjazdu"
        @ct-term-details-open=${this.handleDetailsOpen}
      >
        ${this.terms.map(term => {
          const availability =
            getAvailability(term)

          const pilot =
            getPilotById(term.pilotId)

          // Lot i hotel pochodzą z konkretnego terminu, a opis pilota
          // z jego wspólnego profilu w pilots.js.
          const expandedDetails = {
            flight: term.expandedDetails?.flight ?? null,
            hotel: term.expandedDetails?.hotel ?? null,
            pilot: pilot?.details ?? null,
          }

          const reservationUrl =
            typeof term.reservationUrl === 'string'
              ? term.reservationUrl.trim()
              : ''

          const hasBookingUrl =
            reservationUrl.length > 0

          const canBook =
            availability.bookingEnabled &&
            hasBookingUrl

          const bookingLabel = hasBookingUrl
            ? availability.bookingLabel
            : 'Rezerwacja niedostępna'

          const currentPrice = Number(
            term.price?.amount
          )

          const shouldShowPriceNote =
            Number.isFinite(currentPrice) &&
            currentPrice ===
              Number(term.priceNotePrice)

          return html`
            <ct-term-card
              .date=${term.displayDate ?? ''}
              .startDate=${term.startDate ?? ''}
              .endDate=${term.endDate ?? ''}
              .group=${term.group ?? ''}
              .airport=${term.airport ?? ''}
              .status=${availability.label}
              .statusVariant=${availability.variant}
              .pilotName=${pilot?.name ?? ''}
              .pilotImage=${pilot?.image ?? ''}
              .pilotImagePosition=${pilot?.imagePosition ?? 'center center'}
              .pilotNameFontSize=${pilot?.nameFontSize ?? '16px'}
              .pilotNameLetterSpacing=${pilot?.nameLetterSpacing ?? 'normal'}
              .pilotLabel=${pilot?.label ?? 'Pilot wyjazdu'}
              .expandedDetails=${expandedDetails}
              .price=${formatCurrentPrice(
                term.price
              )}
              .priceNote=${shouldShowPriceNote
                ? term.priceNote ?? ''
                : formatDepositNote(term.price)}
              .bookingEnabled=${canBook}
              .bookingLabel=${bookingLabel}
              .bookingUrl=${reservationUrl}
            ></ct-term-card>
          `
        })}
      </section>
    `
  }
}

if (!customElements.get('ct-offer-terms')) {
  customElements.define(
    'ct-offer-terms',
    CtOfferTerms
  )
}
